import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmationTextBox from '../../../components/ConfirmationPrompt';
import { showDialogueBox, hideDialogueBox } from '../../../components/DialogueBox/actions';
import DynamicTable from '../../Table';
import Icon from "../../../icons";
import { fetchRelationshipsRequest, searchRelationships } from '../../../containers/AdminPanel/features/relationships/relationshipsSlice';
import {
  selectRelationshipsData,
  selectRelationshipsError,
  selectRelationshipsLoading
} from "../../../containers/AdminPanel/features/relationships/relationshipsSelectors";
import useLiveRefresh from '@utils/useLiveRefresh';
import { postRelationshipAccess, deleteRelationshipAccess, updateOrganizationRelationship } from '../../../containers/AdminPanel/api';
import { showToast } from '../../../containers/SpockViewPage/actions/action';
import AddRelationshipModal from './AddRelationshipModal';
import ShareWorkkardModal from './ShareWorkkardModal';
import {
  STATUS_FILTERS,
  RELATIONSHIP_ACTION_STATUS,
  STATUS_TEXT_MAP
} from './constants';
import OrganisationInfo from '../OrgInfo';
import { getOrgId } from '../../../utils/authManager';
import { useModules } from '../../../utils/modules';

/**
 * Normalizes a value to a valid number, returning 0 if invalid.
 * @param {*} value - The value to parse.
 * @returns {number}
 */
const normalizeNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Extracts a readable error message from various error object shapes.
 * @param {object|Error} error - The error object from an API call or catch block.
 * @returns {string}
 */
const getErrorMessage = (error) =>
  error?.response?.data?.errors?.[0]?.error ||
  error?.response?.data?.message ||
  error?.message ||
  'Something went wrong. Please try again.';

/**
 * Helper to dispatch standard toast notifications.
 * @param {function} dispatch - Redux dispatch function.
 * @param {object} options - Toast options { type, message }.
 */
const showRelationshipToast = (dispatch, { type = 'success', message }) => {
  dispatch(showToast({
    message,
    type,
    iconType: type === 'success' ? 'success' : undefined,
    autoClose: true,
    delayTime: 4000,
  }));
};

/**
 * Creates an action button configuration object for the relationship table.
 * @param {string} label - The text label for the action.
 * @param {object} [options] - Additional options like className, tooltip, etc.
 * @returns {object}
 */
const createAction = (label, options = {}) => ({
  label,
  className: options.className || 'btn-cc__primary',
  tooltip: options.tooltip,
  tooltipItems: options.tooltipItems || [],
  totalCount: options.totalCount || 0,
});

/**
 * Helper to display a standard confirmation dialog.
 * @param {object} params - Dialog parameters (dispatch, header, message, onConfirm, onCancel).
 */
const openConfirmDialog = ({ dispatch, header, message, onConfirm, onCancel }) => {
  dispatch(showDialogueBox({
    flag: true,
    component: (
      <ConfirmationTextBox
        class="modalBody"
        message={message}
        header={header}
        onConfirmClick={onConfirm}
        onCancelClick={onCancel}
        confirmationText="Confirm"
        footerClass="modalFooter"
        cancelText="Cancel"
        messageTextClass="colFlex"
      />
    ),
    overlayClassName: "modal",
    contentClassName: "modalContent small animated faster fadeInUpx2",
    shouldCloseOnOverlayClick: true,
  }));
};

/**
 * Determines which action buttons should be visible for a relationship row based on its status.
 * @param {object} row - The relationship data row.
 * @param {string} status - The normalized relationship status.
 * @returns {Array} An array of action configuration objects.
 */
const getRelationshipActionLabels = (row, status) => {
  const labels = [];

  if (status === 'PENDING_TO_ACCEPT') {
    labels.push(createAction('Exclude', { className: 'btn-cc__link btn-cc__link__blue' }));
  } else if (status.startsWith('EXCLUDED')) {
    labels.push(createAction('Include',{ className: 'btn-cc__link btn-cc__link__blue' }));
  } else if (['PENDING', 'PENDING_FROM_RECEIVER'].includes(status)) {
    labels.push(createAction('Accept', { className: 'btn-cc__primary' }));
    labels.push(createAction('Ignore', { className: 'btn-cc__primary__danger' }));
    labels.push(createAction('Exclude', { className: 'btn-cc__link  btn-cc__link__blue' }));
  } else if (status.startsWith('IGNORED')) {
    labels.push(createAction('Accept'));
    labels.push(createAction('Exclude', { className: 'btn-cc__link  btn-cc__link__blue' }));
  }

  if (status === 'ACCEPTED') {    
    if (row?.CanShareWorkkards && !(row?.CanRemoveShareWorkkard)) {
      labels.push(createAction('Share Workkards', { className: 'btn-cc__link  btn-cc__link__blue' }));
    } 
    if (row?.CanRemoveShareWorkkard) {
      labels.push(createAction('Remove Share Workkards', { className: 'btn-cc__link  btn-cc__link__blue' }));
    }
    if (row?.CanManageContracts) {
      const pendingCount = normalizeNumber(row?.PendingCount);
      const expiredCount = normalizeNumber(row?.ExpiredCount);
      const totalCount = pendingCount + expiredCount;
      const tooltipItems = [
        pendingCount > 0 ? {
          text: `${pendingCount} contract${pendingCount > 1 ? 's' : ''}`,
          statusClass: 'status statusFilled open openFilled',
          statusText: 'Pending',
        } : null,
        expiredCount > 0 ? {
          text: `${expiredCount} contract${expiredCount > 1 ? 's' : ''}`,
          statusClass: 'status statusFilled closemidterm closemidtermFilled',
          statusText: 'Expiring',
        } : null,
      ].filter(Boolean);
      labels.push(createAction('Manage Contracts', {
        className: 'btn-cc__link  btn-cc__link__default',
        tooltipItems,
        totalCount,
      }));
    }
  }

  const MODULE_KEY_BY_LABEL = {
    'Exclude': 'admin.relationship.exclude',
    'Include': 'admin.relationship.include',
    'Ignore': 'admin.relationship.ignore',
    'Share Workkards': 'admin.relationship.workkard_sharing',
    'Remove Share Workkards': 'admin.relationship.workkard_sharing',
    'Manage Contracts': 'admin.relationship.manage_contract',
  };
  const { isModuleEnabled } = useModules();
  return labels.filter((action) => {
    const key = MODULE_KEY_BY_LABEL[action.label];
    return !key || isModuleEnabled(key);
  });
};

/**
 * Standardizes the payload structure for relationship API updates.
 * @param {object} row - The relationship data row?.
 * @param {string} status - The new target status.
 * @returns {object} The formatted API payload.
 */
const buildRelationshipPayload = (row, status) => {
  const currentOrgId =  Number(getOrgId());
  const destinationOrgId = row?.DestinationOrg?.id ?? row?.assignee ?? null;
  const ownerId = row?.SourceOrg?.id ?? row?.owner ?? currentOrgId;
  return {
    id: row?.RelationshipId ?? row?.id,
    owner: ownerId,
    status,
    assignee: destinationOrgId
  };
};

/**
 * Component that renders action buttons (Accept, Ignore, Share, etc.) for a given relationship row?.
 * Handles triggering the respective API calls and confirmation dialogs.
 */
const RelationshipActions = ({ row, onShareWorkkards, onRemoveShareWorkkards, onRefresh }) => {
  const dispatch = useDispatch();
  const [activeTooltip, setActiveTooltip] = useState(null);
  const rawStatus = row?.RequestStatus ?? '';
  const status = rawStatus.toUpperCase();

  if (!status) return null;

  const handleAction = (label) => {
    if (
      label === 'Share Workkards'
    ) {
      onShareWorkkards?.(row);
      return;
    }
    if (label === 'Remove Share Workkards') {
      onRemoveShareWorkkards?.(row);
      return;
    }

    const actionStatus = RELATIONSHIP_ACTION_STATUS[label];
    if (!actionStatus) {
      return;
    }
    openConfirmDialog({
      dispatch,
      header: label,
      message: `Are you sure you want to ${label.toLowerCase()} this organization?`,
      onConfirm: async () => {
        try {
          await updateOrganizationRelationship(buildRelationshipPayload(row, actionStatus));
          onRefresh?.();
        } catch (error) {
          console.error('Relationship update failed', error);
        }
        dispatch(hideDialogueBox());
      },
      onCancel: () => dispatch(hideDialogueBox()),
    });
  };

  const actionLabels = getRelationshipActionLabels(row, status);

  if (!actionLabels.length) return null;

  return (
    <div className="rowFlex alignMiddle flexWrap">
      {actionLabels.map((action) => (
        action.label === 'Manage Contracts' ? (
          <div
            key={`${action.label}-${row?.id}`}
            className="tooltip-wrapper"
            onMouseEnter={() => setActiveTooltip(row?.id)}
            onMouseLeave={() => setActiveTooltip(null)}
          >
            <button
              type="button"
              className={action.className}
              onClick={() => handleAction(action.label)}
              title={action.tooltip}
            >
              Manage Contracts{action.totalCount > 0 ? ` (${action.totalCount})` : ''}
              {action.totalCount > 0 && <span className="btn-cc__notification"></span>}
            </button>

            {activeTooltip === row?.id && action.tooltipItems.length > 0 && (
              <div className="tooltip-box bottom">
                {action.tooltipItems.map((item) => (
                  <div className="alignMiddle rowFlex mb" key={`${row?.id}-${item.statusText}`}>
                    <p className="mrNone">{item.text}</p>
                    <span className={`ml ${item.statusClass}`}>{item.statusText}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            key={`${action.label}-${row?.id}`}
            className={action.className}
            onClick={() => handleAction(action.label)}
            title={action.tooltip}
          >
            {action.label}
          </button>
        )
      ))}
    </div>
  );
};

/**
 * Component that renders the three-dot dropdown menu for a relationship row?.
 * Typically includes secondary actions like Enable/Disable Workkard Note and De-associate.
 */
const RelationshipMenu = ({ row, onRefresh, currentOrgId }) => {
  const dispatch = useDispatch();
  const { isModuleEnabled } = useModules();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const shouldShowDisableWorkkardNote = Boolean(row?.CanShowDisableWorkkardNote);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, []);

  const handleDeAssociate = () => {
    openConfirmDialog({
      dispatch,
      header: 'De-associate',
      message: 'Are you sure you want to de-associate this organization?',
      onConfirm: async () => {
        try {
          await updateOrganizationRelationship({
            ...buildRelationshipPayload(row, RELATIONSHIP_ACTION_STATUS['De-associate']),
            deleted: true,
          });
          onRefresh?.();
        } catch (error) {
          console.error('Relationship update failed', error);
        }
        dispatch(hideDialogueBox());
        setShowMenu(false);
      },
      onCancel: () => {
        dispatch(hideDialogueBox());
        setShowMenu(false);
      },
    });
  };

  const handleWorkkardNoteClick = async (e) => {
    e.preventDefault();
    const payload = {
      org_id: currentOrgId,
      target_org_id: row?.OrganizationId ?? null,
      access_type_id: shouldShowDisableWorkkardNote ? 0 : 3,
      share_financial: false,
    };
    const orgName = row?.OrganizationName || row?.Name || row?.name || 'organization';
    const isEnablingNote = !shouldShowDisableWorkkardNote;

    try {
      await postRelationshipAccess(payload);
      showRelationshipToast(dispatch, {
        type: 'success',
        message: isEnablingNote
          ? `Workkard Note enabled for ${orgName}`
          : `Workkard Note disabled for ${orgName}`,
      });
      onRefresh?.();
    } catch (error) {
      showRelationshipToast(dispatch, {
        type: 'warning',
        message: getErrorMessage(error),
      });
      console.error('Failed to update workkard note permission', error);
    } finally {
      setShowMenu(false);
    }
  };

  return (row?.RequestStatus === 'ACCEPTED' ?
    <div
      ref={menuRef}
      className={`dropDown animated noteAction superFaste ${showMenu ? 'active' : ''}`}
    >
      <Icon
        name="more"
        className="icon more"
        onClick={() => setShowMenu((prev) => !prev)}
      />
      <div className={showMenu ? 'dropDownMenu openRight onClick' : 'dropDownMenu openRight'}>
        {isModuleEnabled('admin.relationship.add_workkard_note') && (
          <a href="#" onClick={handleWorkkardNoteClick}>
            {shouldShowDisableWorkkardNote ? 'Disable Workkard Note' : 'Enable Workkard Note'}
          </a>
        )}
        {row?.CanDeAssociateB2BRelationship && isModuleEnabled('admin.relationship.de_associate') && (
          <a href="#" onClick={(e) => { e.preventDefault(); handleDeAssociate(); }} className='warning'>
            De-associate
          </a>
        )}
      </div>
    </div> : ''
  );
};

/**
 * Main Relationships View Component.
 * Manages the data table, search, filtering, and modal dialogs for business relationships.
 */
const Relationships = () => {
  const dispatch = useDispatch();
  const { isModuleEnabled } = useModules();
  const relationships = useSelector(selectRelationshipsData);
  const loading = useSelector(selectRelationshipsLoading);
  const error = useSelector(selectRelationshipsError);
  const userId = useSelector((state) => state.sessionContextOidc?.userView?.id);
  const orgId = useSelector((state) => state.sessionContextOidc?.organizationView?.OrganizationId);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All');

  const searchDebounceRef = useRef(null);

  const openAddRelationshipModal = useCallback(() => {
    dispatch(showDialogueBox({
      flag: true,
      component: (
        <AddRelationshipModal onClose={() => dispatch(hideDialogueBox())} />
      ),
      overlayClassName: 'modal modalOverFlow',
      contentClassName: 'modalContent small animated faster fadeInUpx2',
      shouldCloseOnOverlayClick: true,
    }));
  }, [dispatch]);

  const refreshRelationships = useCallback(() => {
    if (searchTerm) {
      dispatch(searchRelationships(searchTerm));
      return;
    }
    dispatch(fetchRelationshipsRequest());
  }, [dispatch, searchTerm, currentPage]);

  const openShareModal = useCallback((row) => {
    dispatch(showDialogueBox({
      flag: true,
      component: (
        <ShareWorkkardModal
          onClose={() => dispatch(hideDialogueBox())}
          onConfirm={async ({ selectedDurationId, shareFinancial }) => {
            const payload = {
              org_id: orgId,
              target_org_id: row?.OrganizationId ?? null,
              access_type_id: selectedDurationId,
              share_financial: shareFinancial
            };
            try {
              await postRelationshipAccess(payload);
              refreshRelationships();
            } catch (error) {
              console.error('Failed to share workkards', error);
            } finally {
              dispatch(hideDialogueBox());
            }
          }}
        />
      ),
      overlayClassName: 'modal',
      contentClassName: 'modalContent small animated faster fadeInUpx2',
      shouldCloseOnOverlayClick: true,
    }));
  }, [dispatch, refreshRelationships]);

  const openRemoveShareWorkkardsConfirm = useCallback((row) => {
    dispatch(showDialogueBox({
      flag: true,
      component: (
        <ConfirmationTextBox
          class="modalBody"
          header="Remove Workkards sharing"
          message="Selected organization will not be able to view the related workkards."
          onConfirmClick={async () => {
            const payload = {
              id: row?.ShareWkCommunityId ?? null,
              org_id: orgId,
              target_org_id: row?.OrganizationId ?? null,
              access_type_id: row?.ShareWkAccessTypeId ?? null,
            };
            try {
              await deleteRelationshipAccess(payload);
              refreshRelationships();
            } catch (error) {
              console.error('Failed to remove workkard sharing', error);
            } finally {
              dispatch(hideDialogueBox());
            }
          }}
          onCancelClick={() => dispatch(hideDialogueBox())}
          confirmationText="Confirm"
          footerClass="modalFooter"
          cancelText="Cancel"
          messageTextClass="colFlex"
        />
      ),
      overlayClassName: 'modal',
      contentClassName: 'modalContent small animated faster fadeInUpx2',
      shouldCloseOnOverlayClick: true,
    }));
  }, [dispatch, refreshRelationships]);

  useEffect(() => {
    if (searchTerm) return;
    if (!userId) return; // wait for sessionContext to be ready before fetching
    dispatch(fetchRelationshipsRequest());
  }, [dispatch, searchTerm, currentPage, userId]);

  // organization.b2b.* is emitted for both sides of a relationship, so the other
  // org's accept or reject lands here without a refresh. `refreshRelationships`
  // already re-runs the active search instead of clearing it.
  useLiveRefresh(['organization'], () => {
    if (!userId) return;
    refreshRelationships();
  });

  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  const columns = useMemo(
    () => [
      {
        key: 'summary',
        header: 'Organization Summary',
        render: (row) => <OrganisationInfo row={row} showStatus={selectedFilter === 'All'} />,
      },
      isModuleEnabled('admin.relationship.workkard_activity_count') && {
        key: 'workkards',
        header: 'Workkards',
        render: (row) => (
          <div className="rowFlex alignMiddle">
            <div className='mr'>{row?.OpenWorkkards ?? 0} Open and</div>
            <div>{row?.CloseWorkkards ?? 0} Closed</div>
          </div>
        ),
      },
      isModuleEnabled('admin.relationship.workkard_activity_count') && {
        key: 'activities',
        header: 'Activities',
        render: (row) => (
          <div className="rowFlex alignMiddle">
            <div className='mr'>{row?.OpenActivities ?? 0} Open and</div>
            <div>{row?.CloseActivities ?? 0} Closed</div>
          </div>
        ),
      },
      {
        key: 'actions',
        header: 'Relationship Actions',
        render: (row) => (<div className="rowFlex alignMiddle flexWrap">
          <RelationshipActions
            row={row}
            onShareWorkkards={openShareModal}
            onRemoveShareWorkkards={openRemoveShareWorkkardsConfirm}
            onRefresh={refreshRelationships}
          />
          <RelationshipMenu
            row={row}
            onRefresh={refreshRelationships}
            currentOrgId={orgId}
          />
          </div>
        ),
      },
      // {
      //   key: 'menu',
      //   header: '',
      //   render: (row) => (
          
      //   ),
      //   width: 40,
      // },
    ].filter(Boolean),
    [openShareModal, refreshRelationships, isModuleEnabled, selectedFilter],
  );

  // Map display tab labels back to API status values for filtering.
  const filterStatusMap = useMemo(() => {
    return Object.entries(STATUS_TEXT_MAP).reduce((acc, [status, label]) => {
      if (!acc[label]) {
        acc[label] = new Set();
      }
      acc[label].add(status);
      return acc;
    }, {});
  }, []);

  const filterCounts = useMemo(() => {
    const counts = { All: relationships.length };
    STATUS_FILTERS.forEach((filter) => {
      if (filter === 'All') return;
      const statuses = filterStatusMap[filter];
      if (!statuses?.size) {
        counts[filter] = 0;
        return;
      }
      counts[filter] = relationships.filter((row) =>
        statuses.has((row?.RequestStatus || '').toUpperCase()),
      ).length;
    });
    return counts;
  }, [relationships, filterStatusMap]);

  // Apply the selected status filter to the relationship list
  const filteredRelationships = useMemo(() => {
    if (selectedFilter === 'All') return relationships;
    const targetStatuses = filterStatusMap[selectedFilter];
    if (!targetStatuses?.size) return [];
    return relationships.filter((row) =>
      targetStatuses.has((row?.RequestStatus || '').toUpperCase()),
    );
  }, [relationships, selectedFilter, filterStatusMap]);

  const handleFilterClick = (e, filter) => {
    e.preventDefault();
    setSelectedFilter(filter);
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setSelectedFilter('All');
    setCurrentPage(1);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      dispatch(searchRelationships(value));
    }, 300);
  };

  const isEmptyState = !loading && !filteredRelationships?.length && !error;
  
  return (
    <div className="">
      <div className='colFlex admin__header'>
        <div className="rowFlex alignMiddle rowSpaceBetween">
          <h1>Manage Relationships</h1>
          <div className="org_access__add--user wth-4">
            <div className="formGroup mrNone">
              {isModuleEnabled('admin.relationship.search') && <div className='search rowFlex mr'>
                <input
                  type="search"
                  placeholder="Search by name or location"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <div className="search__icon">
                  <ul className="rowFlex">
                    <li>
                      {searchTerm ? (
                        <a href="#" onClick={(e) => { e.preventDefault(); handleSearchChange({ target: { value: '' } }); }}>
                          <Icon name="close" className="icon iconSmall" />
                        </a>
                      ) : (
                        <a href="#" onClick={(e) => e.preventDefault()}>
                          <Icon name="search" className="icon searchIcon" />
                        </a>
                      )}
                    </li>
                  </ul>
                </div>
              </div>}
              {isModuleEnabled('admin.relationship.add_relationship') && <button
              type="button"
              className="btn-cc__primary"
              onClick={openAddRelationshipModal}
            >
              Add Relationship
            </button>}
            </div>

          </div>
        </div>
        <div className="tabNav mt__1">
          <div className="tabs">
            {STATUS_FILTERS.map((option) => (
              <a
                key={option}
                href="#"
                className={selectedFilter === option ? 'active' : ''}
                onClick={(e) => handleFilterClick(e, option)}
              >
                {`${option} (${filterCounts[option] ?? 0})`}
              </a>
            ))}
          </div>
        </div>
      </div>
      <div>
        {loading && <div>Loading relationships...</div>}
        {!loading && error && <div>{error}</div>}
        {isEmptyState && <div>No relationships found.</div>}
      </div>
      <div className="admin__container__data admin__container__data--relationships">
      {!loading && filteredRelationships.length > 0 && (
        <DynamicTable columns={columns} data={filteredRelationships} />
      )}
      </div>
    </div>
  );
};

export default Relationships;
